const motionItemDefinitions = [
    { container: '.home-system__stages', items: '.home-system-stage', progressive: true },
    { container: '.home-climbing__workflows', items: '.home-climbing__workflow' },
    { container: '.home-use-cases__list', items: '.home-use-case' },
    { container: '.home-engineering__proofs', items: '.home-proof' },
    { container: '.page-step-list', items: '.page-step', progressive: true },
    { container: '.page-topic-list', items: '.page-topic' },
    { container: '.page-app-sequence', items: '.page-app-sequence__item', progressive: true },
    { container: '.product-feature-list', items: '.product-feature' },
    { container: '.product-app-story__screens', items: '.app-screen' },
    { container: '.product-metric-list', items: '.product-metric' },
    { container: '.product-specification-list', items: '.product-specification' },
    { container: '.product-package-list', items: '.product-package-item' },
    { container: '.support-directory__groups', items: '.support-directory__group' },
    { container: '.search-results', items: '.search-result' },
    { container: '.collections', items: '.collection-card' },
    { container: '.collection-products', items: '.collection-product' },
    { container: '.blog-page', items: '.blog-card' },
    { container: '.cart-page__lines', items: '.cart-line' }
];

const revealSelector = ['[data-motion-force]', '.home-system__media', '.home-climbing__media', '.home-progress__media', '.home-progress__screen', '.home-engineering__media', '.page-story__media', '.page-topic__media', '.page-app-sequence .app-screen__media', '.product-feature__media', '.product-metric__visual', '.product-app-story .app-screen__media'].join(', ');

const formSelector = ['.article-comment-form', '.cart-page__form', '.contact-form', '.launch-list-form', '.newsletter-form', '.password-page__form', '.product-form form'].join(', ');

class AtiraMotionManager {
    constructor() {
        this.handleEntries = this.handleEntries.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);
        this.handleSectionUnload = this.handleSectionUnload.bind(this);
        this.observedElements = new Set();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.targetMetadata = new WeakMap();

        if (!this.reducedMotion && typeof IntersectionObserver === 'function') {
            this.observer = new IntersectionObserver(this.handleEntries, {
                rootMargin: '0px 0px -12% 0px',
                threshold: 0.18
            });
        }
    }

    connect() {
        this.enhance(document);
        this.focusFormFeedback(document);
        document.addEventListener('shopify:section:load', this.handleSectionLoad);
        document.addEventListener('shopify:section:unload', this.handleSectionUnload);
    }

    enhance(root) {
        this.enhanceSequences(root);
        this.enhanceReveals(root);
        this.enhanceForms(root);
    }

    enhanceSequences(root) {
        motionItemDefinitions.forEach((definition) => {
            root.querySelectorAll(definition.container).forEach((container) => {
                if (container.dataset.motionEnhanced === 'true') {
                    return;
                }

                const items = Array.from(container.querySelectorAll(`:scope > ${definition.items}`));

                if (items.length === 0) {
                    return;
                }

                container.dataset.motionEnhanced = 'true';
                if (definition.progressive) {
                    container.dataset.motionSequence = 'true';
                }

                items.forEach((item, index) => {
                    item.dataset.motionItem = 'true';
                    const type = definition.progressive ? 'sequence' : 'item';
                    this.registerTarget(item, { container, index, type });
                });
            });
        });
    }

    enhanceReveals(root) {
        root.querySelectorAll(revealSelector).forEach((element) => {
            if (element.dataset.motionReveal === 'true') {
                return;
            }

            element.dataset.motionReveal = 'true';
            this.registerTarget(element, { type: 'reveal' });
        });
    }

    enhanceForms(root) {
        root.querySelectorAll(formSelector).forEach((form) => {
            if (form.dataset.motionEnhanced === 'true') {
                return;
            }

            form.dataset.motionEnhanced = 'true';
            form.addEventListener('submit', (event) => {
                const fallbackSubmitButton = form.querySelector('[type="submit"]');
                const submitButton = event.submitter instanceof HTMLElement ? event.submitter : fallbackSubmitButton;

                form.setAttribute('aria-busy', 'true');
                submitButton?.setAttribute('data-motion-submitting', 'true');
            });
        });
    }

    registerTarget(element, metadata) {
        if (this.reducedMotion || !this.observer) {
            element.dataset.motionState = 'visible';
            return;
        }

        element.dataset.motionState = 'waiting';
        this.targetMetadata.set(element, metadata);
        this.observedElements.add(element);
        this.observer.observe(element);
    }

    handleEntries(entries) {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            const metadata = this.targetMetadata.get(entry.target);
            entry.target.dataset.motionState = 'visible';

            if (metadata?.type === 'sequence') {
                this.updateSequence(metadata.container, metadata.index);
            }

            this.observer.unobserve(entry.target);
            this.observedElements.delete(entry.target);
        });
    }

    updateSequence(container, activeIndex) {
        const currentProgress = Number(container.dataset.motionProgress ?? -1);
        const nextProgress = Math.max(currentProgress, activeIndex);

        container.dataset.motionProgress = String(nextProgress);
        container.querySelectorAll(':scope > [data-motion-item]').forEach((item, index) => {
            if (index < nextProgress) {
                item.dataset.motionPhase = 'complete';
                return;
            }

            item.dataset.motionPhase = index === nextProgress ? 'active' : 'upcoming';
        });
    }

    focusFormFeedback(root) {
        const feedback = root.querySelector('[data-form-feedback][tabindex="-1"]');

        if (!(feedback instanceof HTMLElement)) {
            return;
        }

        window.requestAnimationFrame(() => {
            feedback.focus();
        });
    }

    handleSectionLoad(event) {
        this.enhance(event.target);
    }

    handleSectionUnload(event) {
        const section = event.target;

        this.observedElements.forEach((element) => {
            if (!section.contains(element)) {
                return;
            }

            this.observer?.unobserve(element);
            this.observedElements.delete(element);
        });
    }
}

const motionManager = new AtiraMotionManager();
motionManager.connect();
